// frontend/src/hooks/useZoneMutations.ts — the zones write surface (create ·
// rename · disable · reactivate · set-default · delete), mirroring useSiteTypeMutations.
// Each invalidates the `['zones', projectId]` prefix on success so list + detail
// refetch (FRONTEND-PATTERNS §Mutations + invalidation).
//
// Errors are NOT caught here — they bubble so the form shell / ConfirmDialog map
// the backend `code` (ZONE_NAME_EXISTS) through errors.json. 401 is the apiClient's
// job (ADR-003). No invalidate(['me']) — reference data doesn't affect the session.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { CreateZoneRequest, UpdateZoneRequest } from '@ems-app/schemas/zones/requests';
import type { CreatedZoneResponse } from '@ems-app/schemas/zones/responses';
import { apiClient } from '../lib/apiClient.js';
import { zonesKeys } from './useZones.js';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateZones(projectId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: zonesKeys.all(projectId) });
}

/** POST …/zones — create a zone; returns its id. `is_default` clears the prior. */
export function useCreateZone(
  projectId: string,
): UseMutationResult<CreatedZoneResponse, Error, CreateZoneRequest> {
  const invalidate = useInvalidateZones(projectId);
  return useMutation({
    mutationFn: async (body) =>
      (await apiClient.post<CreatedZoneResponse>(`/projects/${projectId}/zones`, body)).data,
    onSuccess: invalidate,
  });
}

/** PATCH …/zones/:id — rename (name only). */
export function useUpdateZone(
  projectId: string,
): UseMutationResult<void, Error, { zoneId: number; body: UpdateZoneRequest }> {
  const invalidate = useInvalidateZones(projectId);
  return useMutation({
    mutationFn: async ({ zoneId, body }) => {
      await apiClient.patch(`/projects/${projectId}/zones/${zoneId}`, body);
    },
    onSuccess: invalidate,
  });
}

/** POST …/zones/:id/{disable,reactivate} — lifecycle toggle (soft, unguarded). */
export function useSetZoneDisabled(
  projectId: string,
): UseMutationResult<void, Error, { zoneId: number; disabled: boolean }> {
  const invalidate = useInvalidateZones(projectId);
  return useMutation({
    mutationFn: async ({ zoneId, disabled }) => {
      await apiClient.post(`/projects/${projectId}/zones/${zoneId}/${disabled ? 'disable' : 'reactivate'}`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** POST …/zones/:id/set-default — promote to the project default (clears the prior). */
export function useSetZoneDefault(projectId: string): UseMutationResult<void, Error, number> {
  const invalidate = useInvalidateZones(projectId);
  return useMutation({
    mutationFn: async (zoneId) => {
      await apiClient.post(`/projects/${projectId}/zones/${zoneId}/set-default`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** DELETE …/zones/:id — only while nothing uses it; a refusal (409 REFERENCE_IN_USE) names the uses, read back by
 *  `ReferenceDeleteDialog`. */
export function useDeleteZone(projectId: string): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`/projects/${projectId}/zones/${id}`);
    },
    // The lists refetch — NOT the gone zone's own read: refetched, it would 404 and the apiClient's not-found
    // redirect would beat the peek's close (the teams' delete precedent); a REFUSED delete leaves it, and it re-reads too.
    onSettled: refreshOnSettled((_d, id, err) => void qc.invalidateQueries({ queryKey: zonesKeys.all(projectId), predicate: err ? undefined : (q) => q.queryKey[2] !== id })),
  });
}
